import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  isFinishAtType,
  isRemainingTimeType,
} from "@circle-react/helpers/paywallCustomizationHelpers";
import { ACTION_TYPE } from "@circle-react/types/PaywallCustomization";
import { FieldContainer } from "../components/FieldContainer";
import { localeRoot } from "./../../constants";
import { ActionTypeOptions } from "./Fields/ActionTypeOption";
import { CountdownTypeRadio } from "./Fields/CountdownTypeRadio";
import { DateToFinishDatePicker } from "./Fields/DateToFinishDatePicker";
import { DisplayedTextInput } from "./Fields/DisplayedTextInput";
import { RedirectToInput } from "./Fields/RedirectToInput";
import { RemainingTimeInput } from "./Fields/RemainingTimeInput";

interface CountdownFormProps {
  formRootName: string;
}

const dataTestId = "paywall-customization-countdown-fields";
const defaultFieldClassName = "!mt-0 !py-0";
const localeFields = "builder.countdown.fields";
const translationRoot = [localeRoot, localeFields].join(".");

export const CountdownForm = ({ formRootName }: CountdownFormProps) => {
  const { watch } = useFormContext();

  const fields = useMemo(
    () => ({
      text: [formRootName, "displayed_text"].join("."),
      countdown_type: [formRootName, "countdown_type"].join("."),
      finish_at: [formRootName, "finish_at"].join("."),
      remaining_time: [formRootName, "remaining_time"].join("."),
      displayed_text: [formRootName, "displayed_text"].join("."),
      action: {
        action_type: [formRootName, "action.action_type"].join("."),
        redirect_to: [formRootName, "action.redirect_to"].join("."),
      },
    }),
    [formRootName],
  );

  const [countdownType, actionType] = watch([
    fields.countdown_type,
    fields.action.action_type,
  ]);

  return (
    <FieldContainer
      dataTestId={dataTestId}
      className="gap-y-5 px-6 py-0"
      withoutBorder
    >
      <div className="flex flex-col gap-y-3">
        <CountdownTypeRadio
          name={fields.countdown_type}
          translationRoot={translationRoot}
        />
        {isRemainingTimeType(countdownType) && (
          <RemainingTimeInput
            name={fields.remaining_time}
            formClassName={defaultFieldClassName}
            translationRoot={translationRoot}
          />
        )}
        {isFinishAtType(countdownType) && (
          <DateToFinishDatePicker
            name={fields.finish_at}
            formClassName={defaultFieldClassName}
            translationRoot={translationRoot}
          />
        )}
      </div>
      <DisplayedTextInput
        name={fields.displayed_text}
        translationRoot={translationRoot}
        formClassName={defaultFieldClassName}
      />
      <div className="flex flex-col gap-y-3">
        <ActionTypeOptions
          name={fields.action.action_type}
          translationRoot={translationRoot}
          formClassName={defaultFieldClassName}
        />
        {actionType === ACTION_TYPE.REDIRECT_TO && (
          <RedirectToInput
            name={fields.action.redirect_to}
            translationRoot={translationRoot}
            formClassName={defaultFieldClassName}
          />
        )}
      </div>
    </FieldContainer>
  );
};
